/* Copyright (c) 2022 Xperi. All Rights Reserved. */

import React, { FunctionComponent, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PreviewType, ScreenType } from 'client-code/previews';
import { ContentType } from 'client-code/grid-guide';
import { Diagnostics } from '@tivocorp/tivo-webapp-loggers';
import { NFREvents } from '../../core/enums/NFREvents';
import { NFRLoggings } from '../../core/logging/NFRLoggings';
import { TOfferPreview } from '../../core/types/TOfferPreview';
import { UniqueIdentifier } from '../../core/utils/UniqueIdentifier';
import { LocaleKeys } from '../../core/locale/LocaleKeys';
import { Locale } from '../../core/locale/Locale';
import { AppUtils } from '../../core/AppUtils';
import { GridGuideRow } from '../../core/entities/GridGuideRow';
import { Channel } from '../../core/entities/Channel';
import { GuideCell } from '../../core/entities/GuideCell';
import { Log } from '../../core/logging/Log';
import { VuLogger } from '../../core/logging';
import { DeviceModel } from '../../core/device';
import { ServicesClientImpl } from '../../core/impls/ServicesClientImpl';
import {
    GRID_TIMEBAR_REFRESH_TIME,
    IDLE_GRID_GUIDE_DURATION,
    TURBO_SCROLL_ANIMATION_DURATION,
    PRIMARY_BRANDING_LOGO_WIDTH_1080P,
    PRIMARY_BRANDING_LOGO_HEIGHT_1080P,
    GUIDE_NETWORK_CALL_WAIT_DURATION,
} from '../../config';
import {
    guideHideSpinner,
    guideSetCaretInvisible,
    loadGridGuide,
    loadGridGuideFutureRows,
    loadGridGuidePastRows,
    loadGridGuideRows,
    refreshGridGuide,
} from '../../store/guide/guide.reducers';
import { setHorizontalPositionAction } from '../../store/guide/guidehighlight/guidehighlight.actions';
import { setHorizontalPosition } from '../../store/guide/guidehighlight/guidehighlight.reducers';
import { evaluateCurrentHorizontalSessionIndex } from '../../store/guide/guidenavigation/guidekeyoperations.reducers';
import { fetchAndUpdateChannels } from '../../store/guide/turboscroll/turboscroll.reducers';
import { RootState } from '../../store/root.reducers';
import { VisibilityState } from '../../store/visibilityevents/visibilitystate';
import { setGridGuideOfferPreviewDataAction } from '../../store/guide/guide.actions';
import { setChannelOptions } from '../../store/overlay/channeloptionsoverlay/channeloptionsoverlay.reducers';
import { GridGuideHeader } from './GridGuideHeader';
import { MainGuide } from './MainGuide';
import { GridGuideFooter } from './GridGuideFooter';
import { PastGuide } from './PastGuide';
import { Clock } from '../Clock';
import { ClockTypes } from '../Clock/ClockTypes';
import { PosterImage } from './PosterImage';
import { PreviewPane } from './PreviewPane';
import { Whisper } from '../Whisper';
import { ImageLoader } from '../ImageLoader';
import { ImageLoaderTypes } from '../ImageLoader/ImageLoaderTypes';
import { TurboScroll } from './TurboScroll';
import { NoNetworkOverlay } from '../NoNetworkOverlay';
import { AntiburnOsd, Mousetrap } from '../common';
import { InteractiveLayer } from './InteractiveLayer';
import { CursorManager } from '../../core/navigation';
import { GuideError, PreviewError } from '../../core/error';
import { NetworkManager } from '../../core/network/NetworkManager';
import {
    clearOverlayTaskController,
    hideOverlayDialog,
} from '../../store/overlaydialog/overlaydialog.reducers';
import { VideoManager } from '../../core/videoplayer';
import { Spinner } from '../VideoPlayerUI/Spinner';
import { hideSpinner } from '../../store/spinner/spinner.reducers';

import './Guide.scss';

const Guide: FunctionComponent = (): JSX.Element => {
    const TAG: string = 'GrideGuide';
    const [posterImageUrl, setPosterImageUrl] = useState('');
    const [isAdultPosterImage, setIsAdultPosterImage] = useState(false);
    const [mOfferPreview, setMOfferPreview] = useState({});

    const dispatch = useDispatch();
    const gridGuide = useSelector((state: RootState) => state.gridGuide);
    const guideHighlight = useSelector((state: RootState) => state.guideHighlight);
    const { isActive: isAntiburnOsdActive } = useSelector((state: RootState) => state.antiburnOsd);
    const {
        guideRows,
        LIMIT,
        channels,
        pastGuideRows,
        currentTimestamp,
        nextTimestamp,
        isPastGuideMode,
        isChannelsServiceFail,
        futureGuideRows,
        startTime,
        error,
        isSpinnerActive: isGuideSpinnerActive,
    }: {
        guideRows: GridGuideRow[];
        LIMIT: number;
        channels: Channel[];
        pastGuideRows: GridGuideRow[];
        currentTimestamp: Date;
        nextTimestamp: Date;
        isPastGuideMode: boolean;
        isChannelsServiceFail: boolean;
        futureGuideRows: GridGuideRow[];
        startTime: Date;
        error: GuideError;
        isSpinnerActive: boolean;
    } = gridGuide;
    const { horizontalPosition, verticalPosition, horizontalSession, verticalPageCount } =
        guideHighlight;
    const mGuideCells = guideRows[verticalPosition]?.guideCells;
    const offerId = mGuideCells && mGuideCells[horizontalPosition - 1]?.offerId;
    const { isOverlayDialogActive } = useSelector((state: RootState) => state.overlayDialog);
    const { channelsTypeIndex } = useSelector((state: RootState) => state.channelOptionsOverlay);
    const { isTurboScrollActive, isGuidePageScrollActive } = useSelector(
        (state: RootState) => state.turboScroll,
    );
    const { isSpinnerActive } = useSelector((state: RootState) => state.spinner);

    const services = new ServicesClientImpl();
    const primaryBrandingImageUrl = services.getPartnerBrandingImageUrl(
        PRIMARY_BRANDING_LOGO_WIDTH_1080P,
        PRIMARY_BRANDING_LOGO_HEIGHT_1080P,
    );

    const { visibilityState } = useSelector((state: RootState) => state.visibilityEvents);
    const isScreenActive = React.useRef(false);
    const enablePosterImage = React.useRef(false);

    useEffect(() => {
        dispatch(hideSpinner());
        if (isOverlayDialogActive) {
            dispatch(hideOverlayDialog());
            dispatch(clearOverlayTaskController());
        }
        Diagnostics.info(TAG, 'GuideScreen mounted.');
        Log.i(TAG, 'GuideScreen mounted.');
        VuLogger.logGuideScreenEntryEvent();
        dispatch(setChannelOptions());
        dispatch(loadGridGuide());
        if (!DeviceModel.getAppSettings().isVideoWindowEnabled()) enablePosterImage.current = true;
        const videoManagerRef =
            VideoManager.getInstance().addReadyListener(handleOnVideoManagerReady);
        const cursorManagerRef = CursorManager.subscribe(onCursorStateChangeCallback);

        return () => {
            Diagnostics.info(TAG, 'GuideScreen unmounted.');
            Log.i(TAG, 'GuideScreen unmounted.');
            VuLogger.logGuideScreenExitEvent();
            CursorManager.unsubscribe(cursorManagerRef);
            VideoManager.getInstance().removeReadyListener(videoManagerRef);
        };
    }, []);

    useEffect(() => {
        const currentGuideRow = guideRows && guideRows[verticalPosition];
        const guideCells = currentGuideRow && currentGuideRow.guideCells;

        const hasOnlyOneGuideCell = guideCells && guideCells.length === 1;
        if (hasOnlyOneGuideCell && horizontalPosition > 0) {
            dispatch(setHorizontalPositionAction(1));
        }

        const currentGuideCell = guideCells && guideCells[horizontalPosition - 1];
        const sPosterImageUrl = currentGuideCell && currentGuideCell.posterImageUrl;

        // For Adult content we can implement code here
        const isAdult = currentGuideCell && currentGuideCell.isAdult;

        const fetchOfferPreviewTimeoutId = setTimeout(() => {
            setPosterImageUrl(sPosterImageUrl);

            if (isAdult) setIsAdultPosterImage(true);
            else setIsAdultPosterImage(false);

            fetchOfferPreview();
        }, GUIDE_NETWORK_CALL_WAIT_DURATION);

        return () => {
            clearTimeout(fetchOfferPreviewTimeoutId);
        };
    }, [guideRows, horizontalPosition, verticalPosition]);

    useEffect(() => {
        const guidePageTimeoutId = setTimeout(() => {
            dispatch(loadGridGuidePastRows());
            dispatch(loadGridGuideRows());
            dispatch(loadGridGuideFutureRows());
        }, GUIDE_NETWORK_CALL_WAIT_DURATION);

        return () => {
            clearTimeout(guidePageTimeoutId);
        };
    }, [verticalPageCount]);

    useEffect(() => {
        const timerId = setInterval(() => {
            const currentTime = new Date();
            const endTime = AppUtils.addMinutesToDateTime(startTime, IDLE_GRID_GUIDE_DURATION);
            if (
                currentTime.getTime() > endTime.getTime() ||
                currentTime.getTime() < startTime.getTime()
            ) {
                dispatch(refreshGridGuide());
            }
        }, GRID_TIMEBAR_REFRESH_TIME);

        return () => {
            clearInterval(timerId);
        };
    }, [startTime]);

    useEffect(() => {
        const currentGuideRow = guideRows && guideRows[verticalPosition];
        const guideCells = currentGuideRow && currentGuideRow.guideCells;

        const hasValidGuideCells = guideCells && guideCells.length > 0;
        if (hasValidGuideCells && horizontalPosition > 0) {
            let currentIndex = evaluateCurrentHorizontalSessionIndex(
                guideCells,
                currentTimestamp,
                horizontalSession,
            );
            currentIndex = currentIndex === -1 ? 0 : currentIndex;
            dispatch(setHorizontalPosition(currentIndex + 1));
        }
    }, [guideRows]);

    useEffect(() => {
        let turboScrollTimerId: any = null;
        let guidePageScrollTimerId: any = null;
        if (isTurboScrollActive) {
            turboScrollTimerId = setTimeout(() => {
                dispatch(fetchAndUpdateChannels());
            }, TURBO_SCROLL_ANIMATION_DURATION);
        }
        if (isGuidePageScrollActive) {
            guidePageScrollTimerId = setTimeout(() => {
                dispatch(fetchAndUpdateChannels());
            }, TURBO_SCROLL_ANIMATION_DURATION);
        }
        return () => {
            clearTimeout(turboScrollTimerId);
            clearTimeout(guidePageScrollTimerId);
        };
    }, [isTurboScrollActive, channels, isGuidePageScrollActive]);

    useEffect(() => {
        if (isScreenActive && isScreenActive.current) {
            if (visibilityState === VisibilityState.ON_PAUSE) {
                VuLogger.logGuideScreenPauseEvent();
            }
            if (visibilityState === VisibilityState.ON_RESUME) {
                VuLogger.logGuideScreenResumeEvent();
            }
        }
        return () => {
            isScreenActive.current = true;
        };
    }, [visibilityState]);

    useEffect(() => {
        if (NetworkManager.isNetworkDisconnected() && isGuideSpinnerActive) {
            dispatch(guideHideSpinner());
        }
    }, [NetworkManager.isNetworkDisconnected(), isGuideSpinnerActive]);

    const generateGridGuideRows = (): GridGuideRow[] => {
        const isValidGridGuideRows =
            guideRows.length > 0 && pastGuideRows.length > 0 && futureGuideRows.length > 0;
        if (!isValidGridGuideRows) return [];
        let sGuideRows = guideRows.map((guideRow: GridGuideRow) => ({
            stationId: guideRow.stationId,
            guideCells: JSON.parse(JSON.stringify(guideRow.guideCells)),
        }));

        sGuideRows = sGuideRows.map((sGuideRow: GridGuideRow, index) => {
            const aPastGuideRow: GridGuideRow = pastGuideRows[index];
            const aPastGuideRowCells: GuideCell[] = aPastGuideRow?.guideCells;
            const aPastGuideRowLastCell: GuideCell =
                aPastGuideRowCells[aPastGuideRowCells?.length - 1];
            const aFutureGuideRow: GridGuideRow = futureGuideRows[index];
            const aFutureGuideRowCells: GuideCell[] = aFutureGuideRow?.guideCells;
            const aFutureGuideRowFirstCell: GuideCell = aFutureGuideRowCells[0];
            const aGuideRow = sGuideRow;
            aGuideRow.guideCells.unshift(aPastGuideRowLastCell);
            const lastIndex = aGuideRow.guideCells.length - 1;
            const endTime = AppUtils.addSecondsToDateTime(
                aGuideRow.guideCells[lastIndex].startTime,
                aGuideRow.guideCells[lastIndex].duration,
            );
            if (endTime.getTime() <= nextTimestamp.getTime())
                aGuideRow.guideCells.push(aFutureGuideRowFirstCell);
            return aGuideRow;
        });

        return sGuideRows;
    };

    const mGridGuideRows: GridGuideRow[] = generateGridGuideRows();

    const fetchOfferPreview = () => {
        Diagnostics.info(TAG, 'inside -> fetchOfferPreview');
        NFRLoggings.log(NFREvents.START_LOADING_PREVIEW);
        const networkCallId = UniqueIdentifier.getNextGuidePreviewNetworkCallId();
        if (!offerId) {
            if (horizontalPosition === 0) {
                const hasChannel = channels.length > 0 && channels[verticalPosition];
                const channel: Channel = channels[verticalPosition];
                setMOfferPreview({
                    contentPreview: {
                        title: hasChannel ? `${channel.callSign} ${channel.channelNumber}` : '',
                        streamType: hasChannel ? channel.videoResolution : '',
                        description: hasChannel ? channel.affiliate || channel.name : '',
                    },
                });
            } else {
                setMOfferPreview({ ...generateOfferPreviewData() });
            }
        } else {
            setMOfferPreview({});

            services
                .fetchOfferPreview(offerId, ScreenType.GuideHeader, networkCallId)
                .then((response: TOfferPreview) => {
                    let offerPreview = {};
                    if (
                        response.networkCallId === UniqueIdentifier.getGuidePreviewNetworkCallId()
                    ) {
                        if (Object.keys(response.offerPreview).length > 0) {
                            offerPreview = response.offerPreview;
                            dispatch(setGridGuideOfferPreviewDataAction(offerPreview));
                        } else {
                            offerPreview = generateOfferPreviewData();
                        }
                        setMOfferPreview({ ...offerPreview });
                    }
                    NFRLoggings.log(NFREvents.FINISH_LOADING_PREVIEW);
                })
                .catch((previewError) => {
                    const guidePreviewError = previewError?.response?.data;
                    const serviceError = new PreviewError(
                        guidePreviewError?.message,
                        guidePreviewError?.code,
                    );
                    VuLogger.logAppError(serviceError);
                    const offerPreview = generateOfferPreviewData();
                    setMOfferPreview(offerPreview);
                    dispatch(setGridGuideOfferPreviewDataAction(offerPreview));
                    Diagnostics.error(TAG, 'Failed to fetch offer preview data');
                    Diagnostics.debug(TAG, previewError);
                    Log.e(TAG, `OfferPreview: ${previewError}`);
                    NFRLoggings.log(NFREvents.FAILURE_LOADING_PREVIEW, serviceError);
                });
        }
    };

    const generateOfferPreviewData = (): object => {
        if (!mGuideCells) {
            return {};
        }
        const isAdult = mGuideCells[horizontalPosition - 1]?.isAdult;
        const programTitle: string = isAdult
            ? Locale.getString(LocaleKeys.GUIDE_TITLE_HIDDEN)
            : mGuideCells[horizontalPosition - 1]?.title || '';

        const programDescription = isAdult
            ? Locale.getString(LocaleKeys.GUIDE_DESCRIPTION_HIDDEN)
            : Locale.getString(LocaleKeys.LIVE_TV_FULL_INFO_BANNER_DESCRIPTION_NOT_AVAILABLE);
        const movieYear = mGuideCells[horizontalPosition - 1]?.movieYear;
        const programContentType: string = mGuideCells[horizontalPosition - 1]?.contentType || '';
        const previewType =
            programContentType === ContentType.Movie
                ? PreviewType.MoviePreview
                : programContentType;

        const offerPreviewData = {
            contentPreview: {
                title: programTitle,
                description: programDescription,
                movieYear,
                previewType,
            },
        };
        return offerPreviewData;
    };

    const onCursorStateChangeCallback = (state) => {
        if (state === false) {
            dispatch(guideSetCaretInvisible());
        }
    };

    const handleOnVideoManagerReady = async () => {
        try {
            if (DeviceModel.getAppSettings().isVideoWindowEnabled()) {
                const playable = await services.getLastPlayedOrFallbackToFirstByChannelNumber(
                    DeviceModel.getAppSettings().getLastTunedChannelNumber(),
                );
                VideoManager.getInstance().mount();
                VideoManager.getInstance().startPlayback(playable);
            }
        } catch (blunder) {
            Log.d(TAG, 'handleOnVideoManagerReady ', blunder);
        }
    };

    return (
        <React.Fragment>
            {!isGuideSpinnerActive ? <NoNetworkOverlay /> : null}
            <div id="guide" className="grid-guide-main" data-testid="guide">
                <Mousetrap className="top-section" value={''}>
                    <div
                        className={`preview-pane ${isAdultPosterImage ? 'adjust-padding' : ''}`}
                        data-testid="guide-preview-pane"
                    >
                        <div className="top-row">
                            <ImageLoader
                                src={primaryBrandingImageUrl}
                                imageType={ImageLoaderTypes.PARTNER_IMAGE_PRIMARY_BRANDING}
                                dataTestId="guide-primary-branding"
                            />
                            <Clock type={ClockTypes.GUIDE_CLOCK} />
                        </div>
                        {!isChannelsServiceFail &&
                        !isTurboScrollActive &&
                        !isGuidePageScrollActive ? (
                            <PreviewPane offerPreview={mOfferPreview} />
                        ) : null}
                    </div>
                    <PosterImage
                        imageUrl={enablePosterImage.current ? posterImageUrl : ''}
                        isAdult={isAdultPosterImage}
                    />
                </Mousetrap>
                <div className="container-white">
                    <Mousetrap value={''}>
                        <GridGuideHeader />
                    </Mousetrap>
                    {isPastGuideMode ? (
                        <PastGuide
                            limit={LIMIT}
                            channels={channels}
                            guideRows={mGridGuideRows}
                            currentTimestamp={currentTimestamp}
                            nextTimestamp={nextTimestamp}
                            visibilityState={visibilityState}
                        />
                    ) : (
                        <MainGuide
                            limit={LIMIT}
                            channels={channels}
                            guideRows={mGridGuideRows}
                            currentTimestamp={currentTimestamp}
                            nextTimestamp={nextTimestamp}
                            visibilityState={visibilityState}
                        />
                    )}
                    {isTurboScrollActive || isGuidePageScrollActive ? (
                        <div
                            className={`turbo-scroll-container${
                                (isPastGuideMode && ' past-guide-mode') || ''
                            }`}
                            data-testid="turbo-scroll"
                        >
                            <TurboScroll
                                channelNumber={channels[verticalPosition]?.channelNumber || ''}
                            />
                        </div>
                    ) : null}
                </div>
                <GridGuideFooter
                    selectedChannelType={Locale.getString(channelsTypeIndex)}
                    highlightId={AppUtils.createHighlightId(horizontalPosition, verticalPosition)}
                    optionsMessage={
                        horizontalPosition === 0
                            ? Locale.getString(LocaleKeys.GUIDE_SELECT_OPTIONS_MESSAGE)
                            : Locale.getString(LocaleKeys.GUIDE_SELECT_PRESS_AND_HOLD_MESSAGE)
                    }
                    settingsMessageFirstPart={Locale.getString(
                        LocaleKeys.GUIDE_SETTINGS_MESSAGE_FIRST_PART,
                    )}
                    settingsMessageMiddlePart={Locale.getString(
                        LocaleKeys.GUIDE_SETTINGS_MESSAGE_MIDDLE_PART,
                    )}
                    settingsMessageLastPart={Locale.getString(
                        LocaleKeys.GUIDE_SETTINGS_MESSAGE_LAST_PART,
                    )}
                />
                <Whisper
                    message={
                        error?.getDescription() ||
                        Locale.getString(LocaleKeys.GUIDE_WHISPER_MESSAGE)
                    }
                />
            </div>
            {isAntiburnOsdActive && <AntiburnOsd />}
            {DeviceModel.isWebOsPlatform() && CursorManager.isCursorAppeared() ? (
                <InteractiveLayer />
            ) : null}
            {/* TODO: Spinner component needs to be moved to a common place */}
            {isSpinnerActive ? (
                <div className="grid-guide-spinner">
                    <Spinner message="" />
                </div>
            ) : null}
        </React.Fragment>
    );
};

export default Guide;
